import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid
} from '@mui/material';
import {
  Flag as FlagIcon,
  CardGiftcard as CardGiftcardIcon,
  ShowChart as ShowChartIcon,
  EmojiEvents as EmojiEventsIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import LevelDisplay from '../components/LevelDisplay';

const KidDashboardPage = () => {
  const { currentKid, userType } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  // Redirect if not a kid user
  useEffect(() => {
    if (userType !== 'kid' && currentKid) {
      navigate(`/user/${userId}/targets`);
    }
  }, [userType, currentKid, navigate, userId]);

  if (!user) {
    return null;
  }

  const quickActions = [
    {
      icon: <FlagIcon sx={{ fontSize: 40 }} />,
      title: 'My Targets',
      description: 'See and complete your tasks',
      color: '#6366f1',
      onClick: () => navigate(`/user/${userId}/targets`),
    },
    {
      icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
      title: 'My Gifts',
      description: 'View and redeem rewards',
      color: '#f59e0b',
      onClick: () => navigate(`/user/${userId}/points`),
    },
    {
      icon: <ShowChartIcon sx={{ fontSize: 40 }} />,
      title: 'My Stats',
      description: 'Track your progress',
      color: '#10b981',
      onClick: () => navigate(`/user/${userId}/statistics`),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: 'My Friends',
      description: 'See friends and invite others',
      color: '#ec4899',
      onClick: () => navigate(`/user/${userId}/friends`),
    },
  ];

  return (
    <Box>
      {/* Welcome Banner */}
      <Card sx={{
        mb: 4,
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: 'white',
        borderRadius: 3,
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Hi, {user.name}!
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
            Ready to earn some points?
          </Typography>

          {/* Stats Row */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <LevelDisplay level={user.level ?? 0} size="medium" />
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  Level {user.level}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700}>
                  {user.totalPoints}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Total Points
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#fbbf24' }}>
                  {user.coins}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Coins
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Typography variant="h5" gutterBottom fontWeight={600}>
        What would you like to do?
      </Typography>
      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={action.onClick}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    bgcolor: `${action.color}15`,
                    color: action.color,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Motivation Message */}
      <Card sx={{ mt: 4, borderRadius: 3, bgcolor: 'grey.50' }}>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#f59e0b' }} />
            Complete targets to earn more coins and unlock awesome rewards!
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default KidDashboardPage;